import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { FileUpload } from '@cw-ui/form-components';

import { COMMON_FILE_EXTENSIONS, FILE_ACCEPT_ALL_MIME } from '../../../constants';

const resolveAcceptTypes = accept => {
  if (!accept?.length || accept.includes(FILE_ACCEPT_ALL_MIME)) {
    return COMMON_FILE_EXTENSIONS;
  }

  return accept;
};

const formatMaxFilesMessage = maxFiles => {
  if (!maxFiles) {
    return null;
  }

  if (maxFiles === 1) {
    return 'You can upload 1 file.';
  }

  return `You can upload up to ${maxFiles} files.`;
};

// @cw-ui FileUpload defaults to spacing(6) padding and a 38px icon — compact for this form.
const FILE_UPLOAD_COMPACT_SX = {
  dropZoneSx: {
    py: 2,
    px: 2,
    '& > div': {
      gap: 1
    },
    '& [data-testid="file-upload-icon"]': {
      height: 28,
      width: 28
    }
  }
};

const FileField = ({ field, value, onChange, onBlur, errorText, disabled = false }) => {
  const { maxFiles, maxSizeMb, accept } = field.validation ?? {};
  const acceptTypes = useMemo(() => resolveAcceptTypes(accept), [accept]);
  const maxFilesMessage = formatMaxFilesMessage(maxFiles);

  const handleFilesChange = useCallback(
    files => {
      onChange(maxFiles ? files.slice(0, maxFiles) : files);
      onBlur?.();
    },
    [maxFiles, onChange, onBlur]
  );

  // Required by @cw-ui FileUpload's useFileUpload hook — it calls onError
  // unconditionally when the `files` prop is non-empty (e.g. after upload).
  const handleError = useCallback(() => {}, []);

  return (
    <Box sx={{ width: '100%' }} onBlur={onBlur}>
      <Typography
        variant="subtitle2"
        component="label"
        htmlFor={field.id}
        sx={{ fontWeight: 700, mb: 0.5, display: 'block' }}
      >
        {field.label}
        {field.required && (
          <Typography component="span" color="error.main" aria-hidden>
            {' *'}
          </Typography>
        )}
      </Typography>
      {maxFilesMessage && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {maxFilesMessage}
        </Typography>
      )}
      <FileUpload
        acceptTypes={acceptTypes}
        maxSize={maxSizeMb}
        multiple={(maxFiles ?? 1) > 1}
        files={value ?? []}
        onFilesChange={handleFilesChange}
        onError={handleError}
        fileSize
        disabled={disabled}
        sx={FILE_UPLOAD_COMPACT_SX}
      />
      {errorText && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {errorText}
        </Typography>
      )}
    </Box>
  );
};

FileField.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    required: PropTypes.bool,
    validation: PropTypes.shape({
      maxFiles: PropTypes.number,
      maxSizeMb: PropTypes.number,
      accept: PropTypes.arrayOf(PropTypes.string)
    })
  }).isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  errorText: PropTypes.string,
  disabled: PropTypes.bool
};

export default FileField;
